/**
 * Product Form Validation Schemas - Story 6.3: Create Add Product Form
 * Zod validation schemas for multi-step product creation form
 */

import { z } from 'zod';
import { ProductCategory } from '../types/productForm.types';
import { FORM_CONFIG } from '../constants/productFormConstants';

// ============================================================================
// Step 1: Product Details Schema
// ============================================================================

/**
 * Product Details Validation Schema
 * Validates basic product information: name, category, and description
 */
export const productDetailsSchema = z.object({
  name: z
    .string()
    .min(
      FORM_CONFIG.MIN_PRODUCT_NAME_LENGTH,
      `Product name must be at least ${FORM_CONFIG.MIN_PRODUCT_NAME_LENGTH} characters`
    )
    .max(
      FORM_CONFIG.MAX_PRODUCT_NAME_LENGTH,
      `Product name cannot exceed ${FORM_CONFIG.MAX_PRODUCT_NAME_LENGTH} characters`
    )
    .trim(),
  category: z.nativeEnum(ProductCategory, {
    errorMap: () => ({ message: 'Please select a valid category' }),
  }),
  description: z
    .string()
    .min(
      FORM_CONFIG.MIN_DESCRIPTION_LENGTH,
      `Description must be at least ${FORM_CONFIG.MIN_DESCRIPTION_LENGTH} characters`
    )
    .max(
      FORM_CONFIG.MAX_DESCRIPTION_LENGTH,
      `Description cannot exceed ${FORM_CONFIG.MAX_DESCRIPTION_LENGTH} characters`
    )
    .trim(),
});

export type ProductDetailsSchemaType = z.infer<typeof productDetailsSchema>;

// ============================================================================
// Step 2: Pricing Schema
// ============================================================================

/**
 * Base Pricing Fields Schema
 * Individual field validators for pricing information
 */
const pricingFields = {
  price: z
    .number({
      required_error: 'Price is required',
      invalid_type_error: 'Price must be a number',
    })
    .positive('Price must be greater than 0')
    .max(99999999, 'Price cannot exceed 99,999,999'),
  unit: z
    .string()
    .min(1, 'Unit is required')
    .max(50, 'Unit name cannot exceed 50 characters')
    .trim(),
  quantity: z
    .number({
      required_error: 'Quantity is required',
      invalid_type_error: 'Quantity must be a number',
    })
    .int('Quantity must be a whole number')
    .positive('Quantity must be greater than 0')
    .max(999999, 'Quantity cannot exceed 999,999'),
  minimumOrderQuantity: z
    .number({
      invalid_type_error: 'Minimum order quantity must be a number',
    })
    .int('Minimum order quantity must be a whole number')
    .positive('Minimum order quantity must be at least 1')
    .optional()
    .nullable(),
};

/**
 * Pricing Validation Schema
 * Validates price, unit, quantity, and minimum order quantity information
 */
export const pricingSchema = z.object(pricingFields).refine(
  (data) => {
    // If both are provided, minimum must not exceed available quantity
    if (data.minimumOrderQuantity !== null && data.minimumOrderQuantity !== undefined && data.quantity) {
      return data.minimumOrderQuantity <= data.quantity;
    }
    return true;
  },
  {
    message: 'Minimum order quantity cannot exceed available quantity',
    path: ['minimumOrderQuantity'],
  }
);

export type PricingSchemaType = z.infer<typeof pricingSchema>;

// ============================================================================
// Step 3: Images Schema
// ============================================================================

/**
 * Single Image Schema
 * Validates individual image properties
 * File can be null for existing images in edit mode (marked with an id)
 */
export const imageSchema = z.object({
  file: z.instanceof(File).nullable(),
  preview: z.string().min(1, 'Image preview is required'),
  id: z.string().optional(),
});

/**
 * Images Array Validation Schema
 * Validates the array of product images with size and type constraints
 *
 * Validation rules:
 * - Images are optional (minimum 0)
 * - Maximum 5 images allowed
 * - Each image must be <= 5MB
 * - Only JPEG, PNG, and WebP formats allowed
 */
export const imagesSchema = z.object({
  images: z
    .array(imageSchema)
    .min(0, 'Images are optional')
    .max(FORM_CONFIG.MAX_IMAGES, `Maximum ${FORM_CONFIG.MAX_IMAGES} images allowed`)
    .refine(
      (images) =>
        // Skip validation for null files (existing images in edit mode)
        images.every((img) => !img.file || img.file.size <= FORM_CONFIG.MAX_FILE_SIZE_BYTES),
      {
        message: `Each image must be less than ${FORM_CONFIG.MAX_FILE_SIZE_MB}MB`,
      }
    )
    .refine(
      (images) =>
        // Skip validation for null files (existing images in edit mode)
        images.every((img) =>
          !img.file || (FORM_CONFIG.ALLOWED_IMAGE_TYPES as readonly string[]).includes(img.file.type)
        ),
      {
        message: 'Only JPEG, PNG, and WebP images are allowed',
      }
    ),
});

export type ImagesSchemaType = z.infer<typeof imagesSchema>;

// ============================================================================
// Step 4: Location Schema
// ============================================================================

/**
 * Location Validation Schema
 * Validates delivery/pickup location details
 *
 * Validation rules:
 * - State is required
 * - District is required
 * - Pincode must be exactly 6 digits
 * - Address is optional but limited to 500 characters
 */
export const locationSchema = z.object({
  state: z.string().min(1, 'State is required').trim(),
  district: z.string().min(1, 'District is required').trim(),
  pincode: z
    .string()
    .regex(/^\d{6}$/, 'Pincode must be exactly 6 digits')
    .trim(),
  address: z
    .string()
    .max(500, 'Address cannot exceed 500 characters')
    .trim()
    .optional()
    .or(z.literal('')),
});

export type LocationSchemaType = z.infer<typeof locationSchema>;

// ============================================================================
// Complete Form Schema
// ============================================================================

/**
 * Complete Product Form Schema
 * Combines all step schemas for final validation before submission
 * Note: Location fields are optional (removed from form flow)
 */
export const completeProductFormSchema = z.object({
  // Step 1: Product Details
  name: productDetailsSchema.shape.name,
  category: productDetailsSchema.shape.category,
  description: productDetailsSchema.shape.description,
  // Step 2: Pricing
  price: pricingFields.price,
  unit: pricingFields.unit,
  quantity: pricingFields.quantity,
  minimumOrderQuantity: pricingFields.minimumOrderQuantity,
  // Step 3: Images
  images: imagesSchema.shape.images,
  // Location fields (optional - not part of form flow)
  state: z.string().optional().default(''),
  district: z.string().optional().default(''),
  pincode: z.string().optional().default(''),
  address: z.string().optional().default(''),
});

export type CompleteProductFormSchemaType = z.infer<typeof completeProductFormSchema>;

// ============================================================================
// API Request Schema
// ============================================================================

/**
 * Create Product Request Schema
 * Validates the final payload sent to the API
 * Note: Location fields are now optional
 */
export const createProductRequestSchema = z.object({
  name: z.string().min(3).max(100).trim(),
  category: z.nativeEnum(ProductCategory),
  description: z.string().min(20).max(2000).trim(),
  price: z.number().positive().max(99999999),
  unit: z.string().min(1).max(50).trim(),
  quantity: z.number().int().positive().max(999999),
  minimumOrderQuantity: z.number().int().positive().optional().nullable(),
  state: z.string().trim().optional(),
  district: z.string().trim().optional(),
  pincode: z.string().optional(),
  address: z.string().max(500).trim().optional(),
  imageIds: z.array(z.string()).optional(),
});

export type CreateProductRequestSchemaType = z.infer<typeof createProductRequestSchema>;

// ============================================================================
// Validation Helper Functions
// ============================================================================

/**
 * Step schemas array for indexed access
 * Maps step number (1-indexed) to corresponding schema
 * Note: Only 3 steps now (location step removed)
 */
const stepSchemas = [
  productDetailsSchema,
  pricingSchema,
  imagesSchema,
];

/**
 * Validate a specific step's data
 * @param step - Step number (1-4)
 * @param data - Data to validate
 * @returns Promise resolving to validated data or throwing ZodError
 */
export async function validateStep<T>(step: number, data: unknown): Promise<T> {
  if (step < 1 || step > stepSchemas.length) {
    throw new Error(`Invalid step number: ${step}. Must be between 1 and ${stepSchemas.length}`);
  }

  const schema = stepSchemas[step - 1];
  return schema.parseAsync(data) as Promise<T>;
}

/**
 * Safely validate a specific step's data
 * @param step - Step number (1-4)
 * @param data - Data to validate
 * @returns Promise resolving to SafeParseResult
 */
export async function safeValidateStep(
  step: number,
  data: unknown
): Promise<z.SafeParseReturnType<unknown, unknown>> {
  if (step < 1 || step > stepSchemas.length) {
    return {
      success: false,
      error: new z.ZodError([
        {
          code: z.ZodIssueCode.custom,
          path: [],
          message: `Invalid step number: ${step}. Must be between 1 and ${stepSchemas.length}`,
        },
      ]),
    };
  }

  const schema = stepSchemas[step - 1];
  return schema.safeParseAsync(data);
}

/**
 * Validate the complete form before submission
 * @param data - Complete form data
 * @returns Promise resolving to validated data or throwing ZodError
 */
export async function validateCompleteForm(
  data: unknown
): Promise<CompleteProductFormSchemaType> {
  return completeProductFormSchema.parseAsync(data);
}

/**
 * Safely validate the complete form
 * @param data - Complete form data
 * @returns Promise resolving to SafeParseResult
 */
export async function safeValidateCompleteForm(
  data: unknown
): Promise<z.SafeParseReturnType<unknown, CompleteProductFormSchemaType>> {
  return completeProductFormSchema.safeParseAsync(data);
}

/**
 * Extract field errors from ZodError
 * @param error - ZodError instance
 * @returns Object mapping field names to error messages
 */
export function extractFieldErrors(error: z.ZodError): Record<string, string> {
  const errors: Record<string, string> = {};

  for (const issue of error.issues) {
    const path = issue.path.join('.');
    // Only keep the first error for each field
    if (!errors[path]) {
      errors[path] = issue.message;
    }
  }

  return errors;
}

/**
 * Check if a file is a valid image
 * @param file - File to validate
 * @returns Object with isValid flag and optional error message
 */
export function validateImageFile(file: File): {
  isValid: boolean;
  error?: string;
} {
  if (!(FORM_CONFIG.ALLOWED_IMAGE_TYPES as readonly string[]).includes(file.type)) {
    return {
      isValid: false,
      error: `Invalid file type: ${file.type}. Only JPEG, PNG, and WebP are allowed.`,
    };
  }

  if (file.size > FORM_CONFIG.MAX_FILE_SIZE_BYTES) {
    const sizeMB = (file.size / (1024 * 1024)).toFixed(2);
    return {
      isValid: false,
      error: `File size (${sizeMB}MB) exceeds maximum allowed size of ${FORM_CONFIG.MAX_FILE_SIZE_MB}MB.`,
    };
  }

  return { isValid: true };
}

/**
 * Validate multiple image files
 * @param files - Array of files to validate
 * @param existingCount - Number of existing images
 * @returns Object with valid files and any errors
 */
export function validateImageFiles(
  files: File[],
  existingCount: number = 0
): {
  validFiles: File[];
  errors: string[];
} {
  const validFiles: File[] = [];
  const errors: string[] = [];

  const remainingSlots = FORM_CONFIG.MAX_IMAGES - existingCount;

  if (files.length > remainingSlots) {
    errors.push(
      `Cannot add ${files.length} images. Only ${remainingSlots} slot(s) remaining.`
    );
    // Still validate the first N files that can fit
    files = files.slice(0, remainingSlots);
  }

  for (const file of files) {
    const validation = validateImageFile(file);
    if (validation.isValid) {
      validFiles.push(file);
    } else if (validation.error) {
      errors.push(`${file.name}: ${validation.error}`);
    }
  }

  return { validFiles, errors };
}
